import { useState, useEffect } from "react";
import { FaBars, FaTimes, FaPhoneAlt } from "react-icons/fa";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../assets/bubuschool.png";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { name: "Home", to: "home" },
    { name: "AboutUS", to: "founder" },
    { name: "Our Products", to: "courses" },
    { name: "Services", to: "daycare" },
    { name: "Gallery", to: "gallery" },
    { name: "Facilities", to: "facilities" },

  ];

  return (
    <motion.nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white shadow-lg'
          : 'bg-white shadow-md'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <motion.div
            className="flex items-center space-x-3"
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
          >
            <img
              src={Logo}
              alt="Smart Educare Centre"
              className="w-16 h-16 object-contain"
            />
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold font-platypi text-primary-700">
                Smart Educare
              </h1>
              <p className="text-xs text-primary-500 font-medium">
                Centre
              </p>
            </div>
          </motion.div>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-8">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={item.to}
                  smooth={true}
                  duration={500}
                  className="menu-item cursor-pointer font-platypi font-medium text-primary-700 hover:text-primary-500"
                  activeClass="text-primary-500"
                  spy={true}
                  offset={-100}
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </div>

          {/* CTA Button */}
          <motion.div
            className="hidden lg:flex items-center space-x-4"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <a
              href="tel:+919834387679"
              className="flex items-center space-x-2 bg-gradient-primary text-white px-6 py-3 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 group"
            >
              <span className="group-hover:animate-bounce">Call Now</span>
            </a>
          </motion.div>
          {/* Mobile Menu Button */}
          <motion.button
            className="lg:hidden p-2 rounded-xl bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors duration-200"
            onClick={toggleMenu}
            whileTap={{ scale: 0.95 }}
          >
            {isMenuOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={toggleMenu}
            />

            {/* Mobile Menu */}
            <motion.div
              className="fixed top-0 right-0 h-full w-80 max-w-[90vw] bg-white shadow-2xl z-50 lg:hidden overflow-y-auto"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
            >
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 h-full flex flex-col">
                {/* Mobile Header */}
                <div className="flex items-center justify-between pb-6 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <img src={Logo} alt="Logo" className="w-12 h-12" />
                    <div>
                      <h2 className="font-bold text-primary-700 font-platypi">Smart Educare</h2>
                      <p className="text-xs text-primary-500">Centre</p>
                    </div>
                  </div>
                  <button
                    onClick={toggleMenu}
                    className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <FaTimes className="text-gray-600" />
                  </button>
                </div>

                {/* Mobile Menu Items */}
                <div className="flex-1 space-y-2">
                  {menuItems.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Link
                        to={item.to}
                        smooth={true}
                        duration={500}
                        className="block py-3 px-4 text-base sm:text-lg font-medium text-gray-700 hover:bg-primary-50 hover:text-primary-600 rounded-xl transition-all duration-200 cursor-pointer"
                        onClick={toggleMenu}
                        activeClass="bg-primary-100 text-primary-600"
                        spy={true}
                        offset={-100}
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>

                {/* Mobile CTA */}
                <motion.div
                  className="pt-4 border-t border-gray-200 space-y-3 mt-auto"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 }}
                >
                  <a
                    href="tel:+919834387679"
                    className="flex items-center justify-center space-x-2 w-full bg-gradient-primary text-white py-4 rounded-xl font-semibold hover:shadow-glow transition-all duration-300"
                  >
                    <span>📞 +91 9834387679</span>
                  </a>

                  <div className="text-center">
                    <p className="text-sm text-gray-600">Ready to enroll?</p>
                    <p className="text-lg font-semibold gradient-text">Call us now!</p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
